import React, { useState, Children, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ClipboardList, User, Users, FileSignature, CreditCard, Check, Upload, Clock, XCircle, CheckCircle, FileText, Download, Lock, AlertCircle, X, Award } from 'lucide-react';
type DocStatus = 'awaiting_upload' | 'under_review' | 'approved' | 'rejected';
interface ProfileField {
  label: string;
  value: string;
  filled: boolean;
}
interface OnboardingState {
  profileFields: ProfileField[];
  pdfGenerated: boolean;
  documentStatus: DocStatus;
  paymentComplete: boolean;
}
interface MembershipOnboardingProps {
  onNavigateToProfile?: () => void;
}
export function MembershipOnboarding({
  onNavigateToProfile
}: MembershipOnboardingProps) {
  const [state, setState] = useState<OnboardingState>({
    profileFields: [{
      label: "Ім'я",
      value: 'Микола',
      filled: true
    }, {
      label: 'Прізвище',
      value: 'Сало',
      filled: true
    }, {
      label: 'По батькові',
      value: 'Іванович',
      filled: true
    }, {
      label: 'Дата народження',
      value: '14.05.1985',
      filled: true
    }, {
      label: 'Поштова адреса',
      value: '',
      filled: false
    }, {
      label: 'Регіон',
      value: 'Харківська область',
      filled: true
    }, {
      label: 'Клуб',
      value: 'СК "Вертикаль" (Харків)',
      filled: true
    }, {
      label: 'Альпінізм',
      value: 'КМС',
      filled: true
    }, {
      label: 'Скелелазіння',
      value: 'I розряд',
      filled: true
    }, {
      label: 'Льодолазіння',
      value: 'II розряд',
      filled: true
    }],
    pdfGenerated: false,
    documentStatus: 'under_review',
    paymentComplete: false
  });
  const filledCount = state.profileFields.filter(f => f.filled).length;
  const totalFields = state.profileFields.length;
  const profileComplete = filledCount === totalFields;
  // Calculate progress
  let completedSteps = 0;
  if (state.pdfGenerated) completedSteps++;
  if (state.documentStatus === 'approved') completedSteps++;
  if (state.paymentComplete) completedSteps++;
  const progressPercentage = completedSteps / 3 * 100;
  // Step Status Logic
  const step1Status = state.pdfGenerated ? 'completed' : 'active';
  const step2Status = !state.pdfGenerated ? 'locked' : state.documentStatus === 'approved' ? 'completed' : 'active';
  const step3Status = state.documentStatus !== 'approved' ? 'locked' : state.paymentComplete ? 'completed' : 'active';
  // Animation variants
  const contentVariants = {
    hidden: {
      opacity: 0,
      height: 0,
      marginTop: 0
    },
    visible: {
      opacity: 1,
      height: 'auto',
      marginTop: 16,
      transition: {
        duration: 0.3,
        ease: 'easeInOut'
      }
    }
  };
  const checklistVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.06
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 6
    },
    visible: {
      opacity: 1,
      y: 0
    }
  };
  const toggleProfileComplete = () => {
    if (profileComplete) {
      setState({
        ...state,
        profileFields: state.profileFields.map((f, i) => i === 4 ? {
          ...f,
          value: '',
          filled: false
        } : f),
        pdfGenerated: false
      });
    } else {
      setState({
        ...state,
        profileFields: state.profileFields.map(f => f.filled ? f : {
          ...f,
          value: 'вул. Хрещатик, 22, кв. 45, Київ, 01001',
          filled: true
        })
      });
    }
  };
  // Split fields into personal, membership, and sport qualification groups
  const personalFields = state.profileFields.slice(0, 5);
  const membershipFields = state.profileFields.slice(5, 7);
  const sportFields = state.profileFields.slice(7);
  return <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-[0_4px_24px_rgba(0,0,0,0.04)] border border-slate-100" data-id="element-195">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8" data-id="element-196">
        <div className="flex items-center gap-4" data-id="element-197">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{
          backgroundColor: '#eff8ff'
        }} data-id="element-198">
            <ClipboardList className="w-6 h-6" style={{
            color: '#3b8fd4'
          }} data-id="element-199" />
          </div>
          <div data-id="element-200">
            <h2 className="text-xl font-bold text-slate-900" data-id="element-201">
              Оформлення членства
            </h2>
            <p className="text-sm text-slate-500 mt-0.5" data-id="element-202">
              Пройдіть всі етапи для отримання членства у ФАіСУ
            </p>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1.5" data-id="element-203">
          <span className="text-sm font-bold text-slate-700" data-id="element-204">
            Крок {completedSteps} з 3
          </span>
          <div className="w-32 h-2 bg-slate-100 rounded-full overflow-hidden" data-id="element-205">
            <motion.div className="h-full rounded-full" style={{
            backgroundColor: '#16a34a'
          }} initial={{
            width: 0
          }} animate={{
            width: `${progressPercentage}%`
          }} transition={{
            duration: 0.5,
            ease: 'easeOut'
          }} data-id="element-206" />
          </div>
        </div>
      </div>

      {/* Stepper */}
      <div className="relative" data-id="element-207">
        {/* Vertical Line */}
        <div className="absolute left-[19px] top-8 bottom-12 w-[2px] bg-slate-100" data-id="element-208" />

        {/* Step 1: Profile */}
        <div className="relative z-10 mb-8" data-id="element-209">
          <div className="flex items-start gap-4" data-id="element-210">
            <StepIndicator status={step1Status} number={1} icon={<User className="w-4 h-4" data-id="element-212" />} data-id="element-211" />
            <div className="flex-1 min-w-0 pt-1" data-id="element-213">
              <h3 className={`text-lg font-bold ${step1Status === 'locked' ? 'text-slate-400' : 'text-slate-900'}`} data-id="element-214">
                Заповнення профілю
              </h3>
              <p className="text-sm text-slate-500 mt-1" data-id="element-215">
                Перевірте персональну інформацію для генерації заявки
              </p>

              <AnimatePresence initial={false} data-id="element-216">
                {step1Status !== 'locked' && <motion.div variants={contentVariants} initial="hidden" animate="visible" exit="hidden" className="overflow-hidden" data-id="element-217">
                    <div className="bg-slate-50 rounded-xl p-5 border border-slate-100" data-id="element-218">
                      {/* Warning banner when profile incomplete */}
                      {!profileComplete && <div className="flex items-center gap-2.5 p-3 rounded-lg mb-4 border" style={{
                    backgroundColor: '#fff7ed',
                    borderColor: '#fed7aa'
                  }} data-id="element-219">
                          <AlertCircle className="w-4 h-4 flex-shrink-0" style={{
                      color: '#f97316'
                    }} data-id="element-220" />
                          <p className="text-sm font-medium" style={{
                      color: '#ea580c'
                    }} data-id="element-221">
                            Заповніть всі поля профілю для генерації заявки.{' '}
                            {onNavigateToProfile && <button onClick={onNavigateToProfile} className="underline underline-offset-2 font-semibold hover:no-underline" data-id="element-222">
                                Відкрити профіль →
                              </button>}
                          </p>
                        </div>}

                      {/* Personal Info Section */}
                      <div className="mb-4" data-id="element-223">
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-1.5" data-id="element-224">
                          <User className="w-3.5 h-3.5" data-id="element-225" />
                          Персональна інформація
                        </p>
                        <motion.div variants={checklistVariants} initial="hidden" animate="visible" className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-id="element-226">
                          {personalFields.map((field, i) => {
                        const isFullWidth = field.label === 'Поштова адреса';
                        return <motion.div key={i} variants={itemVariants} className={`flex items-start gap-2.5 p-2.5 rounded-lg border ${isFullWidth ? 'sm:col-span-2' : ''} ${field.filled ? 'bg-white border-slate-100' : 'bg-red-50/50 border-red-200'}`} data-id="element-227">
                                {field.filled ? <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-228">
                                    <Check className="w-3 h-3 text-green-600" data-id="element-229" />
                                  </div> : <div className="w-5 h-5 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-230">
                                    <X className="w-3 h-3 text-red-500" data-id="element-231" />
                                  </div>}
                                <div className="flex-1 min-w-0" data-id="element-232">
                                  <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide leading-tight" data-id="element-233">
                                    {field.label}
                                  </p>
                                  {field.filled ? <p className="text-sm font-medium text-slate-800 truncate mt-0.5" data-id="element-234">
                                      {field.value}
                                    </p> : <p className="text-sm font-medium text-red-500 mt-0.5 flex items-center gap-1" data-id="element-235">
                                      Не заповнено
                                    </p>}
                                </div>
                              </motion.div>;
                      })}
                        </motion.div>
                      </div>

                      {/* Membership Section */}
                      <div className="mb-4" data-id="element-236">
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-1.5" data-id="element-237">
                          <Users className="w-3.5 h-3.5" data-id="element-238" />
                          Місцевий осередок
                        </p>
                        <motion.div variants={checklistVariants} initial="hidden" animate="visible" className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-id="element-239">
                          {membershipFields.map((field, i) => <motion.div key={i} variants={itemVariants} className={`flex items-start gap-2.5 p-2.5 rounded-lg border ${field.filled ? 'bg-white border-slate-100' : 'bg-red-50/50 border-red-200'}`} data-id="element-240">
                              {field.filled ? <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-241">
                                  <Check className="w-3 h-3 text-green-600" data-id="element-242" />
                                </div> : <div className="w-5 h-5 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-243">
                                  <X className="w-3 h-3 text-red-500" data-id="element-244" />
                                </div>}
                              <div className="flex-1 min-w-0" data-id="element-245">
                                <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide leading-tight" data-id="element-246">
                                  {field.label}
                                </p>
                                {field.filled ? <p className="text-sm font-medium text-slate-800 truncate mt-0.5" data-id="element-247">
                                    {field.value}
                                  </p> : <p className="text-sm font-medium text-red-500 mt-0.5" data-id="element-248">
                                    Не заповнено
                                  </p>}
                              </div>
                            </motion.div>)}
                        </motion.div>
                      </div>

                      {/* Sport Qualifications Section */}
                      <div className="mb-5" data-id="element-249">
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-1.5" data-id="element-250">
                          <Award className="w-3.5 h-3.5" data-id="element-251" />
                          Спортивна кваліфікація
                        </p>
                        <motion.div variants={checklistVariants} initial="hidden" animate="visible" className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-id="element-252">
                          {sportFields.map((field, i) => <motion.div key={i} variants={itemVariants} className={`flex items-start gap-2.5 p-2.5 rounded-lg border ${field.filled ? 'bg-white border-slate-100' : 'bg-red-50/50 border-red-200'}`} data-id="element-253">
                              {field.filled ? <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-254">
                                  <Check className="w-3 h-3 text-green-600" data-id="element-255" />
                                </div> : <div className="w-5 h-5 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-256">
                                  <X className="w-3 h-3 text-red-500" data-id="element-257" />
                                </div>}
                              <div className="flex-1 min-w-0" data-id="element-258">
                                <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide leading-tight" data-id="element-259">
                                  {field.label}
                                </p>
                                {field.filled ? <p className="text-sm font-medium text-slate-800 truncate mt-0.5" data-id="element-260">
                                    {field.value}
                                  </p> : <p className="text-sm font-medium text-red-500 mt-0.5" data-id="element-261">
                                    Не заповнено
                                  </p>}
                              </div>
                            </motion.div>)}
                        </motion.div>
                      </div>

                      {/* Footer */}
                      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pt-4 border-t border-slate-200/60" data-id="element-262">
                        <span className={`text-xs font-semibold ${profileComplete ? 'text-slate-500' : 'text-orange-600'}`} data-id="element-263">
                          {filledCount} з {totalFields} полів заповнено
                        </span>
                        {profileComplete ? <button onClick={() => setState({
                      ...state,
                      pdfGenerated: true
                    })} className="px-4 py-2 rounded-xl text-white text-sm font-semibold transition-all hover:opacity-90 active:scale-[0.98]" style={{
                      background: 'linear-gradient(135deg, #3b8fd4, #1d6fa8)',
                      boxShadow: '0 4px 14px rgba(59,143,212,0.35)'
                    }} data-id="element-264">
                            Згенерувати PDF-заявку
                          </button> : <button disabled className="px-4 py-2 rounded-xl text-white text-sm font-semibold opacity-40 cursor-not-allowed" style={{
                      background: 'linear-gradient(135deg, #3b8fd4, #1d6fa8)'
                    }} data-id="element-265">
                            Згенерувати PDF-заявку
                          </button>}
                      </div>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </div>
        </div>

        {/* Step 2: Document */}
        <div className="relative z-10 mb-8" data-id="element-266">
          <div className="flex items-start gap-4" data-id="element-267">
            <StepIndicator status={step2Status} number={2} icon={<FileSignature className="w-4 h-4" data-id="element-269" />} data-id="element-268" />
            <div className="flex-1 min-w-0 pt-1" data-id="element-270">
              <h3 className={`text-lg font-bold ${step2Status === 'locked' ? 'text-slate-400' : 'text-slate-900'}`} data-id="element-271">
                Підписання та завантаження документа
              </h3>
              <p className="text-sm text-slate-500 mt-1" data-id="element-272">
                Роздрукуйте заявку, підпишіть та завантажте скан-копію
              </p>

              <AnimatePresence initial={false} data-id="element-273">
                {step2Status !== 'locked' && <motion.div variants={contentVariants} initial="hidden" animate="visible" exit="hidden" className="overflow-hidden" data-id="element-274">
                    <div className="bg-slate-50 rounded-xl p-5 border border-slate-100" data-id="element-275">
                      <ol className="list-decimal list-inside space-y-2 text-sm font-medium text-slate-700 mb-5" data-id="element-276">
                        <li data-id="element-277">Завантажте PDF-заявку</li>
                        <li data-id="element-278">Роздрукуйте та підпишіть документ</li>
                        <li data-id="element-279">Відскануйте та завантажте копію</li>
                      </ol>

                      {state.documentStatus === 'awaiting_upload' ? <div className="border-2 border-dashed border-slate-200 rounded-xl p-6 flex flex-col items-center justify-center text-center bg-white hover:bg-slate-50/50 transition-colors cursor-pointer" data-id="element-280">
                          <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center mb-3" data-id="element-281">
                            <Upload className="w-5 h-5 text-blue-500" data-id="element-282" />
                          </div>
                          <p className="text-sm font-semibold text-slate-700 mb-1" data-id="element-283">
                            Завантажити скан-копію
                          </p>
                          <p className="text-xs text-slate-400" data-id="element-284">
                            Підтримувані формати: PDF, JPG, PNG (до 5 МБ)
                          </p>
                        </div> : <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-285">
                          <div className="flex items-center gap-3" data-id="element-286">
                            <div className="w-10 h-10 rounded-lg bg-slate-50 flex items-center justify-center border border-slate-100 flex-shrink-0" data-id="element-287">
                              <FileText className="w-5 h-5 text-slate-400" data-id="element-288" />
                            </div>
                            <div data-id="element-289">
                              <p className="text-sm font-semibold text-slate-800" data-id="element-290">
                                zayavka_salo_m.pdf
                              </p>
                              <p className="text-xs text-slate-400" data-id="element-291">1.2 MB</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3" data-id="element-292">
                            {state.documentStatus === 'under_review' && <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-orange-50 text-orange-600 border border-orange-100" data-id="element-293">
                                <Clock className="w-3.5 h-3.5" data-id="element-294" />
                                На перевірці
                              </span>}
                            {state.documentStatus === 'approved' && <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-green-50 text-green-600 border border-green-100" data-id="element-295">
                                <CheckCircle className="w-3.5 h-3.5" data-id="element-296" />
                                Підтверджено
                              </span>}
                            {state.documentStatus === 'rejected' && <div className="flex flex-col sm:items-end gap-2" data-id="element-297">
                                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-red-50 text-red-600 border border-red-100" data-id="element-298">
                                  <XCircle className="w-3.5 h-3.5" data-id="element-299" />
                                  Відхилено
                                </span>
                                <p className="text-xs text-red-500 max-w-[200px] sm:text-right" data-id="element-300">
                                  Нечіткий підпис. Будь ласка, завантажте
                                  якіснішу копію.
                                </p>
                                <button onClick={() => setState({
                          ...state,
                          documentStatus: 'awaiting_upload'
                        })} className="text-xs font-semibold text-blue-600 hover:underline" data-id="element-301">
                                  Завантажити знову
                                </button>
                              </div>}
                          </div>
                        </div>}
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </div>
        </div>

        {/* Step 3: Payment */}
        <div className="relative z-10" data-id="element-302">
          <div className="flex items-start gap-4" data-id="element-303">
            <StepIndicator status={step3Status} number={3} icon={<CreditCard className="w-4 h-4" data-id="element-305" />} data-id="element-304" />
            <div className="flex-1 min-w-0 pt-1" data-id="element-306">
              <h3 className={`text-lg font-bold ${step3Status === 'locked' ? 'text-slate-400' : 'text-slate-900'}`} data-id="element-307">
                Оплата членського внеску
              </h3>
              <p className="text-sm text-slate-500 mt-1" data-id="element-308">
                Сплатіть щорічний членський внесок для активації членства
              </p>

              <AnimatePresence initial={false} data-id="element-309">
                {step3Status !== 'locked' ? <motion.div variants={contentVariants} initial="hidden" animate="visible" exit="hidden" className="overflow-hidden" data-id="element-310">
                    <div className="bg-slate-50 rounded-xl p-5 border border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-5" data-id="element-311">
                      <div data-id="element-312">
                        <p className="text-sm font-medium text-slate-500 mb-1" data-id="element-313">
                          Сума до сплати
                        </p>
                        <div className="flex items-baseline gap-1" data-id="element-314">
                          <span className="text-3xl font-bold text-slate-900" data-id="element-315">
                            ₴ 500
                          </span>
                          <span className="text-sm text-slate-500" data-id="element-316">/ рік</span>
                        </div>
                      </div>
                      {state.paymentComplete ? <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-green-50 text-green-700 font-semibold text-sm border border-green-200" data-id="element-317">
                          <CheckCircle className="w-5 h-5" data-id="element-318" />
                          Оплачено успішно
                        </div> : <button onClick={() => setState({
                    ...state,
                    paymentComplete: true
                  })} className="flex items-center justify-center gap-2 py-3 px-6 rounded-xl text-white font-semibold text-sm transition-all hover:opacity-90 active:scale-[0.98]" style={{
                    background: 'linear-gradient(135deg, #3b8fd4, #1d6fa8)',
                    boxShadow: '0 4px 14px rgba(59,143,212,0.35)'
                  }} data-id="element-319">
                          <CreditCard className="w-4 h-4" data-id="element-320" />
                          Сплатити через LiqPay
                        </button>}
                    </div>
                  </motion.div> : <motion.div variants={contentVariants} initial="hidden" animate="visible" exit="hidden" className="overflow-hidden" data-id="element-321">
                    <div className="flex items-center gap-2 mt-3 text-sm font-medium text-slate-400 bg-slate-50 w-fit px-3 py-2 rounded-lg border border-slate-100" data-id="element-322">
                      <Lock className="w-4 h-4" data-id="element-323" />
                      Доступно після підтвердження документа
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>

      {/* Demo Controls */}
      <div className="mt-10 pt-4 border-t border-slate-100 flex flex-wrap items-center gap-3" data-id="element-324">
        <span className="text-xs font-bold text-slate-400 uppercase tracking-wider mr-2" data-id="element-325">
          Симуляція:
        </span>
        <DemoButton active={!profileComplete} onClick={toggleProfileComplete} data-id="element-326">
          {profileComplete ? 'Прибрати поле' : 'Заповнити все'}
        </DemoButton>
        <div className="w-px h-4 bg-slate-200 mx-1" data-id="element-327" />
        <DemoButton active={state.documentStatus === 'awaiting_upload'} onClick={() => setState({
        ...state,
        profileFields: state.profileFields.map(f => f.filled ? f : {
          ...f,
          value: 'вул. Хрещатик, 22, кв. 45, Київ, 01001',
          filled: true
        }),
        pdfGenerated: true,
        documentStatus: 'awaiting_upload',
        paymentComplete: false
      })} data-id="element-328">
          Очікує файл
        </DemoButton>
        <DemoButton active={state.documentStatus === 'under_review'} onClick={() => setState({
        ...state,
        profileFields: state.profileFields.map(f => f.filled ? f : {
          ...f,
          value: 'вул. Хрещатик, 22, кв. 45, Київ, 01001',
          filled: true
        }),
        pdfGenerated: true,
        documentStatus: 'under_review',
        paymentComplete: false
      })} data-id="element-329">
          На перевірці
        </DemoButton>
        <DemoButton active={state.documentStatus === 'rejected'} onClick={() => setState({
        ...state,
        profileFields: state.profileFields.map(f => f.filled ? f : {
          ...f,
          value: 'вул. Хрещатик, 22, кв. 45, Київ, 01001',
          filled: true
        }),
        pdfGenerated: true,
        documentStatus: 'rejected',
        paymentComplete: false
      })} data-id="element-330">
          Відхилено
        </DemoButton>
        <DemoButton active={state.documentStatus === 'approved' && !state.paymentComplete} onClick={() => setState({
        ...state,
        profileFields: state.profileFields.map(f => f.filled ? f : {
          ...f,
          value: 'вул. Хрещатик, 22, кв. 45, Київ, 01001',
          filled: true
        }),
        pdfGenerated: true,
        documentStatus: 'approved',
        paymentComplete: false
      })} data-id="element-331">
          Підтверджено
        </DemoButton>
        <div className="w-px h-4 bg-slate-200 mx-1" data-id="element-332" />
        <DemoButton active={state.paymentComplete} onClick={() => setState({
        ...state,
        profileFields: state.profileFields.map(f => f.filled ? f : {
          ...f,
          value: 'вул. Хрещатик, 22, кв. 45, Київ, 01001',
          filled: true
        }),
        pdfGenerated: true,
        documentStatus: 'approved',
        paymentComplete: !state.paymentComplete
      })} data-id="element-333">
          {state.paymentComplete ? 'Скинути оплату' : 'Оплатити'}
        </DemoButton>
      </div>
    </div>;
}
// Helper Components
function StepIndicator({
  status,
  number,
  icon
}: {
  status: 'completed' | 'active' | 'locked';
  number: number;
  icon: React.ReactNode;
}) {
  if (status === 'completed') {
    return <div className="relative w-10 h-10 rounded-full bg-green-500 text-white flex items-center justify-center flex-shrink-0 shadow-sm z-10" data-id="element-334">
        <Check className="w-5 h-5" data-id="element-335" />
      </div>;
  }
  if (status === 'active') {
    return <div className="relative w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm z-10" data-id="element-336">
        <div className="absolute inset-0 rounded-full bg-blue-500 animate-ping opacity-20" data-id="element-337" />
        <div className="relative w-full h-full rounded-full flex items-center justify-center text-white font-bold" style={{
        background: 'linear-gradient(135deg, #3b8fd4, #1d6fa8)'
      }} data-id="element-338">
          {number}
        </div>
      </div>;
  }
  return <div className="relative w-10 h-10 rounded-full bg-slate-100 border-2 border-slate-200 text-slate-400 flex items-center justify-center flex-shrink-0 z-10 font-bold" data-id="element-339">
      {number}
    </div>;
}
function DemoButton({
  active,
  onClick,
  children
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return <button onClick={onClick} className={`px-2.5 py-1 rounded text-[10px] font-semibold transition-colors border ${active ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'}`} data-id="element-340">
      {children}
    </button>;
}